import React, { useState } from 'react';
import { toast } from 'react-toastify';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import {
  ICalcomEventType,
  ICreatePropertyBookingLinkRequest,
  IPropertyBookingLink,
  IUpdatePropertyBookingLinkRequest,
  JuniperEventType,
} from '../../types';
import BookingLinkEditableRow from './BookingLinkEditableRow';
import BookingLinkForm, { BookingLinkFormValues } from './BookingLinkForm';
import EventTypeNamingCallout from '../EventTypeNamingCallout/EventTypeNamingCallout';
import './manage-booking-links-dialog.scss';

interface ManageBookingLinksDialogProps {
  propertyId: string;
  propertyName: string;
  kind: JuniperEventType;
  kindLabel: string;
  eventTypes: ICalcomEventType[];
  links: IPropertyBookingLink[];
  isSubmitting: boolean;
  onCreate: (request: ICreatePropertyBookingLinkRequest) => Promise<boolean>;
  onUpdate: (
    bookingLinkId: string,
    request: IUpdatePropertyBookingLinkRequest
  ) => Promise<boolean>;
  onRemove: (bookingLinkId: string) => Promise<boolean>;
  onClose: () => void;
  autoStartAdd?: boolean;
}

const ManageBookingLinksDialog = ({
  propertyId,
  propertyName,
  kind,
  kindLabel,
  eventTypes,
  links,
  isSubmitting,
  onCreate,
  onUpdate,
  onRemove,
  onClose,
  autoStartAdd = false,
}: ManageBookingLinksDialogProps) => {
  const [isAdding, setIsAdding] = useState<boolean>(autoStartAdd);

  const startAdd = (): void => {
    setIsAdding(true);
  };

  const cancelAdd = (): void => {
    setIsAdding(false);
  };

  const saveAdd = async (values: BookingLinkFormValues): Promise<void> => {
    const succeeded: boolean = await onCreate({
      propertyId,
      kind,
      label: values.label,
      calcomEventTypeId: values.calcomEventTypeId,
    });

    if (succeeded) {
      setIsAdding(false);
      toast.success('Booking link added');
    }
  };

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      title={`${kindLabel} booking links`}
      description={propertyName}
      maxWidth="110rem"

      footer={
        <Button color="teal" size="small" onClick={() => onClose()}>
          Done
        </Button>
      }
    >
      <div className="manage-booking-links-dialog">
        <EventTypeNamingCallout kindLabel={kindLabel} />

        <div
          className={`booking-links-list ${
            isAdding ? 'booking-links-list--compact' : ''
          }`}
        >
          {links.length === 0 && (
            <p className="manage-empty">No booking links yet for this kind.</p>
          )}

          {links.map((link: IPropertyBookingLink) => (
            <BookingLinkEditableRow
              key={link.id}
              link={link}
              eventTypes={eventTypes}
              kindLabel={kindLabel}
              isSubmitting={isSubmitting}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </div>

        {isAdding ? (
          <div className="add-booking-link">
            <BookingLinkForm
              eventTypes={eventTypes}
              kindLabel={kindLabel}
              submitLabel="Add link"
              isSubmitting={isSubmitting}
              labelPlaceholder="e.g. 30-minute walkthrough"
              onCancel={cancelAdd}
              onSubmit={saveAdd}
              isEditing={false}
            />
          </div>
        ) : (
          <button
            type="button"
            className="add-booking-link-trigger"
            onClick={startAdd}
          >
            <FontAwesomeIcon glyph="plus" weight="solid" />
            Add booking link
          </button>
        )}
      </div>
    </GenericDialogue>
  );
};

export default ManageBookingLinksDialog;